import React, { FC, useState } from 'react';
import { getDatePickerFormat, getDateFnsLocale } from 'utils/format';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material';
import { ApolloError, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Loader from 'components/shared/loader/loader.component';
import { setInvoiceSentDateTemplate } from 'apollo/templates/invoices';
import { Invoice } from 'models/invoice';

type SetSentDateComponentProps = {
    open: boolean;
    invoiceId: string;
    onDialogClose: () => void;
    onSentDateSet: (error?: ApolloError, invoice?: Invoice) => void;
};

const SetSentDateComponent: FC<SetSentDateComponentProps> = ({
    open,
    invoiceId,
    onDialogClose,
    onSentDateSet,
}: SetSentDateComponentProps) => {
    const { t: translate } = useTranslation(['common', 'invoices']);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());

    const [setInvoiceSentDate, { loading }] = useMutation(setInvoiceSentDateTemplate, {
        onCompleted: ({ setInvoiceSentDate: invoice }) => {
            onSentDateSet(undefined, invoice as Invoice);
        },
        onError: (error: ApolloError) => {
            onSentDateSet(error, undefined);
        },
    });

    const handleClick = async () => {
        await setInvoiceSentDate({ variables: { invoiceId, sentDate: selectedDate } });
    };

    const handleDateChange = (date: Date | null) => {
        setSelectedDate(date as Date);
    };

    return (
        <Dialog maxWidth="md" fullWidth open={open} onClose={onDialogClose}>
            <DialogTitle>{translate('invoices:REGISTER_SENT_DATE')}</DialogTitle>
            <DialogContent>
                <Box mt={1}>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">{translate('invoices:SENT_DATE')}</Typography>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                                <div>
                                    <DatePicker
                                        format={getDatePickerFormat()}
                                        maxDate={new Date()}
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                    />
                                </div>
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            {loading ? (
                <Loader />
            ) : (
                <DialogActions>
                    <Button onClick={onDialogClose}>{translate('common:CANCEL')}</Button>
                    <Button disabled={!selectedDate} onClick={handleClick} variant="contained" type="button">
                        {translate('common:SAVE')}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default SetSentDateComponent;
